import type { CategoryFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/category-filter-state';
import type { MultipleValuesFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/multiple-values-filter-state';
import type { RangeFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/range-filter-state';
import type { SearchableListFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/searchable-list-filter-state';
import type { SingleValueFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/single-value-filter-state';
import type { LocationFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/location-filter-state';
import type { SearchQueryFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/search-query-filter-state';

export type FilterState =
    | CategoryFilterState
    | MultipleValuesFilterState
    | MultipleValuesFilterState<number>
    | RangeFilterState
    | SearchableListFilterState
    | SearchableListFilterState<number>
    | SingleValueFilterState
    | LocationFilterState
    | SearchQueryFilterState;

export const isSearchQueryFilterState = (
    obj: FilterState,
): obj is SearchQueryFilterState => {
    return typeof obj === 'object' && obj !== null && 'query' in obj;
};

export const isCategoryFilterState = (
    obj: FilterState,
): obj is CategoryFilterState =>
    typeof obj === 'object' &&
    obj !== null &&
    'category' in obj &&
    'selectedCategory' in obj &&
    'isRoot' in obj;

export const isMultipleValuesFilterState = (
    obj: FilterState,
): obj is MultipleValuesFilterState => {
    return (
        typeof obj === 'object' &&
        obj !== null &&
        'items' in obj &&
        'selectedItems' in obj
    );
};

export const isRangeFilterState = (
    obj: FilterState,
): obj is RangeFilterState => {
    return (
        typeof obj === 'object' &&
        obj !== null &&
        'value' in obj &&
        'range' in obj &&
        'rangeDivisor' in obj
    );
};

export const isSearchableListFilterState = (
    obj: FilterState,
): obj is SearchableListFilterState => {
    return (
        typeof obj === 'object' &&
        obj !== null &&
        'items' in obj &&
        'selectedItems' in obj &&
        'search' in obj &&
        'preserveSort' in obj &&
        'size' in obj &&
        'hasMoreItems' in obj &&
        'after' in obj
    );
};

export function isFacetFilterState(
    obj: FilterState,
): obj is SearchableListFilterState<number> {
    return (
        isSearchableListFilterState(obj) &&
        typeof obj.items[0]?.value === 'number'
    );
}

export const isLocationFilterState = (
    obj: FilterState,
): obj is LocationFilterState => {
    return typeof obj === 'object' && obj !== null && 'selectedLocation' in obj;
};

export const isSingleValueFilterState = (
    obj: FilterState,
): obj is SingleValueFilterState =>
    typeof obj === 'object' &&
    obj !== null &&
    'label' in obj &&
    'value' in obj &&
    'checked' in obj &&
    'disabled' in obj &&
    'count' in obj;
